import { Device, DeviceRecord, DeviceType, FirmwareRecord, FlashMode } from "../Types";
import DBManager from "./DBManager";

class DeviceManager {
    dbManager: DBManager;
    devices: Device[];
    deviceTypes: DeviceType[];

    constructor(dbManager: DBManager) {
        this.dbManager = dbManager;
        this.devices = [];
        this.deviceTypes = [];
    }

    async findDevice(id: number) {  
        return this.devices.find((device: Device) => device.id === id);
    }

    async getDeviceBySerial(serialNumber: string) {
        return this.devices.find((device: Device) => device.hardwareId === serialNumber);
    }

    clearDevices() {
        this.devices = [];
    }

    async getDevices() {
        const types = await this.getDeviceTypes();
        const flashModes = await this.dbManager.request('/items/modes');
        const modeTypes = await this.dbManager.request('/items/types_modes');
        const patterns = await this.dbManager.request('/items/patterns');
        const modesPatterns = await this.dbManager.request('/items/modes_patterns');

        // Get the devices data
        return this.devices = await this.dbManager.request(`/items/devices`)
        .then((result) => {
            const devices = result.map((device: any) : Device => {
                const deviceType = types.find((type: DeviceType) => type.id === device.type);
                if(!deviceType) console.log('Cannot find type', types, device.type);


                const availableModes: any = [];
                modeTypes.forEach((mode_type: any) => {
                    if(mode_type.types_id === device.type) {
                        const mode: FlashMode = JSON.parse(JSON.stringify(flashModes.find((mode: FlashMode) => mode.id === mode_type.modes_id)));
                        mode.order = mode_type.order;
                        mode.default = mode_type.default;
                        if(mode && !availableModes.find((a: any) => a.id === mode.id)) {
                            if(deviceType?.braking_compatible) {
                                // Push the real patterns
                                const brake_patterns: any = [];
                                modesPatterns.forEach((mode_pattern: any) => {
                                    if(mode_pattern.modes_id === mode.id) {
                                        const pattern: any = patterns.find((p: any) => p.id === mode_pattern.patterns_id);
                                        if(pattern) {
                                            brake_patterns.push(pattern);
                                            if(mode_pattern.default) mode.default_pattern = mode_pattern.patterns_id;
                                        }     
                                    }
                                });
                                mode.brake_patterns_data = brake_patterns;
                            }
                            availableModes.push(mode);
                        }
                    }
                });

                return {
                    id: device.id,
                    owner: device.owner,
                    type: deviceType!,
                    name: device.name,
                    properties: device.properties,
                    availableModes: availableModes,
                    profiles: device.profiles,
                    hardwareId: device.hardware_id,
                    firmware: device.firmware
                }
            });
            // Filter out devices for admins
            return devices.filter((device: Device) => device.owner === this.dbManager.user?.id);
        }).catch((error) => {
            console.error(error);
            return [];
        });
    }

    async getDeviceTypes() {        
        this.deviceTypes = await this.dbManager.request('/items/types')
        .then(async (result) => {
            return result.map((type: any) : DeviceType => ({
                id: type.id,
                status: type.status,
                name: type.name,
                code: type.code,
                product_id: parseInt(type.product_id),
                chip_id: parseInt(type.chip_id),
                power: type.power,
                custom_memory: type.custom_memory,
                eye_saver_compatible: type.eye_saver_compatible,
                braking_compatible: type.braking_compatible,
                sensitivities: type.sensitivities,
                default_sleep: type.default_sleep,
                default_wake: type.default_wake,
                default_smart_lighting: type.default_smart_lighting
            }));
        }).catch((error) => {
            console.error(error);
            return [];
        });
        return this.deviceTypes;
    }

    async getDefaultModes(type: DeviceType) {
        const modeTypes = await this.dbManager.request('/items/types_modes');
        const flashModes = await this.dbManager.request('/items/modes');

        const availableModes: any = [];
        modeTypes.forEach((mode_type: any) => {
            if(mode_type.types_id === type.id) {
                const mode: FlashMode = JSON.parse(JSON.stringify(flashModes.find((mode: FlashMode) => mode.id === mode_type.modes_id)));
                mode.order = mode_type.order;
                mode.default = mode_type.default;
                availableModes.push(mode);
            }
        });
        
        return (availableModes.filter((m: FlashMode) => m.default) ?? [])
            .sort((a: FlashMode, b: FlashMode) => {
                return (a.order ?? 0) - (b.order ?? 0);
            });
    }

    async getType(product_id: number) {
        return this.deviceTypes.find((type: DeviceType) => type.product_id === product_id);
    }

    async checkForNewFirmware(device: Device, allFirmware: FirmwareRecord[], alwaysReturn: boolean = false) {
        if(!device) return null;
        
        const firmware = allFirmware.filter((f: any) => f.device_type === device.type.id);
            //.filter((f: any) => f.id === 10);
        if(!firmware) return null;
        
        // Get firmware with the highest version number
        const newest = firmware.reduce((prev: any, current: any) => {
            return (prev?.version_number > current?.version_number) ? prev : current;
        }, null);

        if(!device.firmware) return newest;

        if(newest && newest.version_number > (firmware.find((f: any) => device?.firmware === f.id)?.version_number ?? -1)) {
            return newest;
        } else {
            return alwaysReturn && newest ? newest : null;
        }
    }

    async addDevice(device: DeviceRecord) {
        const response = await this.dbManager.send(
            '/items/devices', 
            'POST', 
            device
        )        
        
        await this.getDevices();
        return await response.json();
    }

    async deleteDevice(id: number) {
        await this.dbManager.send(
            '/items/devices/' + id, 
            'DELETE'
        );
        await this.getDevices();
    }

    async updateDevice(id: number, device: DeviceRecord) {
        return this.dbManager.request(
            '/items/devices/' + id, 
            'PATCH', 
            device
        ).then(async (reponse) => {
            return reponse;
        }).catch((error) => {
            console.error(error);
        });
    }
}

export default DeviceManager;